import dict from '../lang.js';
import { cModels, cParameters, cMeteograms, cLayers, cDefaults } from '../config.js';
import { press2height, press2FL } from './utils.js';
import validIndicator from './valid-indicator.js';
import datetimeSelector from './datetime-selector.js';
import crosssectionControl from './crosssection-control.js';
import soundingControl from './sounding-control.js';
import SkewT from './skewt.js';
import raspLayer from './rasp-layer.js';
import * as GeoTIFF from 'geotiff';

L.Control.RASPControl = L.Control.extend({
    loadingAnimation: document.getElementById("loadingAnimation"),
    offcanvasContent: document.getElementById("offcanvasContent"),
    plot: document.getElementById("plot"),
    meteogramIcon: L.icon({
        iconUrl: cDefaults.meteogramMarker,
        iconSize: [cDefaults.markerSize, cDefaults.markerSize]
    }),
    onAdd: function(map) {
        this._map = map;
        this._initPanel();

        this.validIndicator = validIndicator().addTo(map);
        this.raspLayer = raspLayer().addTo(map);

        this.plotContent = document.getElementById("plotContent");
        this.plotClose = document.getElementById("plotClose");
        this.plotClose.onclick = () => { this.closePlot(); };

        this.loadingMeta = false;
        this.loadingBlipmap = false;
        this.loadingPlot = false;

        this.lastParameterChoices = {};
        this.renderOptions = {};

        this.datetimeSelector = datetimeSelector(this).addTo(map);
        this.datetimeSelector.init()
            .then(() => {
                this.modelDayChange();
            })
            .catch(() => {
                this.validIndicator.update(dict("dataMissing"), false);
                this.loadingMeta = false;
                this.loadingBlipmap = false;
                this.loadingPlot = false;
                this._hideLoadingAnimationMaybe();
            });

        this.on('modelDayChange', () => { this.modelDayChange(); });
        this.on('timeChange', () => {
            this.update();
            if (this.currentPlot && this.currentPlot.type === "meteogram" && this.currentPlot.viewMode === "sounding") {
                this.updatePlot();
            }
        });

        return this._container;
    },
    dispatchEvent: function(event) {
        this._container.dispatchEvent(event);
    },
    on: function(event, func) {
        this._container.addEventListener(event, func);
    },
    off: function(event, func) {
        if (func) {
            this._container.removeEventListener(event, func);
        } else {
            this._container.removeEventListener(event);
        }
    },
    _initPanel: function() {
        this._container = L.DomUtil.create('div', 'leaflet-control-layers rasp-control');
        this._link = L.DomUtil.create('a', 'leaflet-control-layers-toggle', this._container);
		    this._link.href = '#';
        this._link.title = 'RASP Control';
        if (!L.Browser.android) {
            L.DomEvent.on(this._link, 'mouseenter', this.expand, this);
        }
        if (L.Browser.touch) {
            L.DomEvent.on(this._link, 'click', L.DomEvent.stop);
            L.DomEvent.on(this._link, 'click', this.expand, this);
        } else {
            L.DomEvent.on(this._link, 'focus', this.expand, this);
        }
        this.expand();
		    L.DomEvent.disableClickPropagation(this._container);
		    L.DomEvent.disableScrollPropagation(this._container);

        this._raspPanel = L.DomUtil.create('div', "leaflet-control-layers-list", this._container);

        var parameterDiv = L.DomUtil.create('div', 'mb-2', this._raspPanel);

        this.parameterCategories = L.DomUtil.create('div', 'btn-group d-flex mb-1', parameterDiv);
        this.parameterCategories.setAttribute('data-bs-toggle', 'buttons');
        for (const category in cParameters) {
            var catRadio = L.DomUtil.create('input', 'btn-check', this.parameterCategories);
            var catLabel = L.DomUtil.create('label', 'btn btn-outline-primary', this.parameterCategories);
            catRadio.name = "parameterCategory";
            catRadio.type = "radio";
            catRadio.value = category;
            catLabel.style.cursor = "pointer";
            catLabel.innerHTML += `<svg viewBox="0 0 40 40" width="100%"><use href="img/sprites.svg#${category}" xlink:href="img/sprites.svg#${category}"></use></svg>`;
            catLabel.title = dict("parameterCategory_" + category + "_title");
            catRadio.id = catLabel.title;
            catLabel.htmlFor = catRadio.id;
            if (cDefaults.parameter in cParameters[category]) { // enable the default category
                catRadio.checked = true;
            }
        }
        this.parameterCategories.onchange = () => { this.parameterCategoryChange(); };
        this.parameterCategoryDescription = L.DomUtil.create('div', '', parameterDiv);
        this.parameterSelect = L.DomUtil.create('select', 'form-select form-select-sm fw-bold my-1', parameterDiv);
        this.parameterSelect.onchange = () => { this.parameterChange(); };
        this.parameterSelect.title = dict("parameterSelect_title");

        this.pressSliderInfo = L.DomUtil.create('div', '', parameterDiv);
        this.pressSlider = L.DomUtil.create('input', 'form-range', parameterDiv);
        this.pressSlider.id = 'pressSlider';
        this.pressSlider.type = 'range';
        this.pressSlider.title = dict('press.longname');
        this.pressSlider.value = 0;
        this.pressSliderDatalist = L.DomUtil.create('datalist', '', parameterDiv);
        this.pressSliderDatalist.id = 'press_levels';
        this.pressSlider.setAttribute('list', 'press_levels');
        this.pressSlider.oninput = () => {
            if (this.parameters && this.parameters.press_levels) {
                var press = this.parameters.press_levels[this.pressSlider.value];
                this.pressSliderInfo.innerHTML = dict("press.longname") + ": " + press + " hPa (≈ FL" + press2FL(press) + ", " + (Math.round(press2height(press, "m") / 10) * 10) + " m)";
            }
            this.parameterChange();
        };

        this.cloudToggleDiv = L.DomUtil.create('div', '', parameterDiv); // TODO: generalize parameter rendering options
        var cloudToggleLabel = L.DomUtil.create('label', '', this.cloudToggleDiv);
        cloudToggleLabel.title = dict("clouds");
        this.cloudToggleCheckbox = L.DomUtil.create('input', 'me-1', cloudToggleLabel);
        this.cloudToggleCheckbox.type = 'checkbox';
        this.cloudToggleCheckbox.onchange = (e) => {
            this.renderOptions["withClouds"] = e.target.checked;
            this.parameterChange();
        };
        var cloudToggleText = L.DomUtil.create('span', '', cloudToggleLabel);
        cloudToggleText.innerHTML = dict("clouds");

        var parameterDetails = L.DomUtil.create('details', '', parameterDiv);
        var parameterSummary = L.DomUtil.create('summary', '', parameterDetails);
        parameterSummary.title = dict("parameterDetails_title");
        parameterSummary.innerHTML = dict("parameterDetails_summary");
        this.parameterDescription = L.DomUtil.create('span', 'parameterDescription', parameterDetails);

        var opacityDiv = L.DomUtil.create('div', 'd-flex align-items-center mb-2', this._raspPanel);
        var opacityIconImg = L.DomUtil.create('img', 'me-2', opacityDiv);
        opacityIconImg.src = 'img/opacity.svg';
        opacityIconImg.title = dict("opacityIcon_title");
        opacityIconImg.style.height = '1.2rem';
        var opacitySlider = L.DomUtil.create('input', 'form-range', opacityDiv);
        opacitySlider.id = 'opacitySlider';
        opacitySlider.type = 'range';
        opacitySlider.title = dict("opacityIcon_title");
        opacitySlider.value = cDefaults.opacityLevel * 100;
        opacitySlider.oninput = (e) => { this.raspLayer.setOpacity(e.target.valueAsNumber / 100); };

        if (cDefaults.enableInteractiveTools) {
            var interactiveDiv = L.DomUtil.create('div', 'mb-2', this._raspPanel);
            var interactiveButtons = L.DomUtil.create('div', 'input-group', interactiveDiv);
            var interactiveIcon = L.DomUtil.create('span', 'input-group-text bg-light', interactiveButtons);
            interactiveIcon.innerHTML += `<svg viewBox="0 0 40 40" height="1.5rem" width="1.5rem"><use href="img/sprites.svg#interactive" xlink:href="img/sprites.svg#interactive"></use></svg>`;
            interactiveIcon.title = dict("interactiveIcon_title");
            
            this.crosssectionButton = L.DomUtil.create('button', 'btn btn-outline-primary', interactiveButtons);
            this.crosssectionButton.innerHTML = dict("crosssection");
            this.crosssectionButton.title = dict("crosssection");
            
            this.soundingButton = L.DomUtil.create('button', 'btn btn-outline-primary', interactiveButtons);
            this.soundingButton.innerHTML = dict("sounding");
            this.soundingButton.title = dict("sounding");
            
            this.interactiveHelp = L.DomUtil.create('div', 'mt-1', interactiveDiv);
            this.interactiveStatus = L.DomUtil.create('div', 'text-danger', interactiveDiv);
            
            this.crosssectionControl = crosssectionControl(this);
            this.soundingControl = soundingControl(this);
        }

        if (!cDefaults.alwaysShowMeteograms) {
            var meteogramDiv = L.DomUtil.create('div', '', this._raspPanel);
            var meteogramLabel = L.DomUtil.create('label', '', meteogramDiv);
            meteogramLabel.title = dict("Meteograms");
            this.meteogramCheckbox = L.DomUtil.create('input', 'me-1', meteogramLabel);
            this.meteogramCheckbox.type = 'checkbox';
            this.meteogramCheckbox.onchange = () => { this.toggleMeteograms(); };
            var meteogramText = L.DomUtil.create('span', '', meteogramLabel);
            meteogramText.innerHTML = dict("Meteograms");
        }

        this._collapseLink = L.DomUtil.create('a', 'leaflet-control-collapse-button', this._raspPanel);
        this._collapseLink.innerHTML = '⇱';
        this._collapseLink.title = dict("Minimize panel");
        this._collapseLink.href = '#';
        L.DomEvent.on(this._collapseLink, 'click', () => { this.shallBeCollapsed = true; this.collapse(); }, this);

        this.togglePanelOrOffcanvas();
        window.addEventListener('resize', () => { this.togglePanelOrOffcanvas(); });
    },
    toOffcanvas: function() {
        L.DomEvent.off(this._link, 'mouseenter', this.expand, this);
        this._link.href = "#offcanvas";
        this._link.setAttribute("data-bs-toggle", "offcanvas");
        this.collapse();
        this.isOffcanvas = true;
        this.offcanvasContent.appendChild(this._raspPanel);
        this._collapseLink.style.display = "none";
    },
    toPanel: function() {
        this._container.appendChild(this.offcanvasContent.children[0]);
        this._collapseLink.style.display = "block";
        var offcanvas = bootstrap.Offcanvas.getInstance(document.getElementById("offcanvas"));
        if (offcanvas) {
            offcanvas.hide();
        }
        this.isOffcanvas = false;
        if (offcanvas || !this.shallBeCollapsed) {
            this.expand();
        }
        this._link.removeAttribute("data-bs-toggle");
        this._link.href = "#";
        L.DomEvent.on(this._link, 'mouseenter', this.expand, this);
    },
    togglePanelOrOffcanvas: function() {
        var mapContainer = this._map.getContainer();
        if (mapContainer.offsetWidth < 500 || mapContainer.offsetHeight < 500) {
            if (!this.isOffcanvas) {
                this.toOffcanvas();
                return;
            }
        } else {
            if (this.isOffcanvas) {
                this.toPanel();
                return;
            }
        }
    },
    expand: function () {
        if (!this.isOffcanvas) {
            this.shallBeCollapsed = false;
            L.DomUtil.addClass(this._container, 'leaflet-control-layers-expanded');
        }
    },
    collapse: function () {
        if (!this.isOffcanvas) {
            L.DomUtil.removeClass(this._container, 'leaflet-control-layers-expanded');
        }
    },
    getDataUrls: function(modelDir, parameterKey, parameter, time) {
        var parameterKeys = parameter.composite ? parameter.composite.of : [parameterKey];
        var baseUrls = parameterKeys.map(key => cDefaults.forecastServerResults + "/OUT/" + modelDir + "/" + key + ".");
        if (!parameter.wholeDay) {
            baseUrls = baseUrls.map(base => base + "curr."+time+"lst.d2.");
        }
        var geotiffUrls = baseUrls.map(base => base + "data.tiff");
        var metaUrl = baseUrls[0] + "title.json";
        return {geotiffUrls: geotiffUrls, metaUrl: metaUrl};
    },
    getParameterCategory: function() {
        return this.parameterCategories.querySelector("input:checked").value;
    },
    getParameterKey: function() {
        var category = this.getParameterCategory();
        if (typeof cParameters[category] === "function") {
            return Object.keys(cParameters[category](this.parameters.press_levels[this.pressSlider.value]))[0];
        }
        return this.parameterSelect.value;
    },
    getParameter: function() {
        var category = this.getParameterCategory();
        var parameterKey = this.getParameterKey();
        if (typeof cParameters[category] === "function") {
            return cParameters[category](this.parameters.press_levels[this.pressSlider.value])[parameterKey];
        }
        return cParameters[category][parameterKey];
    },
    modelDayChange: function() {
        var model = this.datetimeSelector.get().model;
        var dir = this.datetimeSelector.get().dir;

        fetch(cDefaults.forecastServerResults + "/OUT/" + dir + "/parameters.json").then(response => {
            return response.json();
        }).then(parameters => {
            this.parameters = parameters;
            this.parameters.available = new Set(this.parameters.parameters);
            if (this.model != model) {
                this._map.fitBounds(this.parameters.boundary, { padding: [20, 20] });
            }
            this.model = model;
            this.pressSliderDatalist.innerHTML = '';
            for (const press of this.parameters.press_levels) {
                this.pressSliderDatalist.innerHTML += `<option value="${press}"></option>`;
            }
            this.raspLayer.renderBoundary(this.parameters.boundary);
            if (this.meteogramOverlay) {
                this.meteogramOverlay.remove();
            }
            this.meteogramOverlay = this.getMeteogramMarkers(model);
            this.toggleMeteograms();
            this.parameterCategoryChange();
            if (this.currentPlot && this.currentPlot.type == "meteogram") {
                this.currentPlot.imageUrl = cDefaults.forecastServerResults + "/OUT/" + dir + "/meteogram_" + this.currentPlot.key + ".png";
                this.currentPlot.image.src = this.currentPlot.imageUrl;
            }
        }).catch(() => {
            this.raspLayer.hideBoundary();
        });
    },
    parameterCategoryChange: function() {
        var category = this.getParameterCategory();
        this.parameterCategoryDescription.innerHTML = dict("parameterCategory_" + category + "_title");
        var currentParameterKey = this.getParameterKey();
        this.parameterSelect.options.length = 0; // Clear all parameters
        if (typeof cParameters[category] === "function") {
            this.parameterSelect.style.display = "none";
            this.pressSlider.style.display = "block";
            this.pressSliderInfo.style.display = "block";
            this.pressSlider.min = 0;
            this.pressSlider.max = this.parameters.press_levels.length - 1;
            this.pressSlider.step = 1;
            this.cloudToggleDiv.style.display = "block";
            this.pressSlider.oninput();
        } else {
            this.parameterSelect.style.display = "block";
            this.pressSlider.style.display = "none";
            this.pressSliderInfo.style.display = "none";
            this.cloudToggleDiv.style.display = "none";
            for (const parameterKey in cParameters[category]) {
                var parameter = cParameters[category][parameterKey];
                var required = parameter.composite ? parameter.composite.of : [parameterKey];
                if (required.reduce((g, r) => g && this.parameters.available.has(r), true)) { // check if we have all parameters that we need
                    this.parameterSelect.add(new Option(cParameters[category][parameterKey].longname, parameterKey));
                    if (!currentParameterKey && parameterKey == cDefaults.parameter || parameterKey == currentParameterKey || parameterKey == this.lastParameterChoices[category]) {
                        this.parameterSelect.options[this.parameterSelect.options.length - 1].selected = true;
                    }
                }
            }
        }
        this.parameterChange();
    },
    parameterChange: function() {
        var category = this.getParameterCategory();
        var parameterKey = this.getParameterKey();
        var parameter = this.getParameter();
        this.parameterDescription.innerHTML = parameter.description;
        this.datetimeSelector.enableTimeControl();
        if (parameter.wholeDay) {
            this.datetimeSelector.disableTimeControl();
        }
        this.update();
        this.lastParameterChoices[category] = parameterKey;
    },
    _loading: function() {
        return this.loadingMeta || this.loadingBlipmap || this.loadingPlot;
    },
    _armLoadingAnimation: function() {
        setTimeout(() => {
            if (this._loading()) {
                this.loadingAnimation.style.visibility = "visible";
            }
        }, cDefaults.loadingAnimationDelay);
    },
    _hideLoadingAnimationMaybe: function() {
        if (!this._loading()) {
            this.loadingAnimation.style.visibility = "hidden";
        }
    },
    update: function() {
        var {model, runDate, day, time, dir} = this.datetimeSelector.get();
        var category = this.getParameterCategory();
        var parameterKey = this.getParameterKey();
        var parameter = this.getParameter();
        var urls = this.getDataUrls(dir, parameterKey, parameter, time);
        this._updateBlipmap(urls.geotiffUrls, parameter, this.renderOptions);
        this._updateMeta(urls.metaUrl, parameter.longname, day);
    },
    _updateMeta: function(metaUrl, parameterLongname, day) {
        fetch(metaUrl + "?timestamp=" + this.parameters.timestamp)
            .then(response => {
                if (response.ok) {
                    return response.json();
                } else {
                    throw Error("metadataMissing");
                }
            })
            .then(validJson => {
                var validDateText = `${validJson["year"]}-${validJson["month"]}-${validJson["day"]}`;
                var runDateText = this.datetimeSelector.get().runDate;
                var timezone = dict(validJson["timezone"]);
                var validTimestamp = `${validDateText} ${validJson["validLocal"]} ${timezone} (${validJson["validZulu"]}Z)`;
                this.validIndicator.update(`${dict("Model run")} ${runDateText} ${validJson["fcstZulu"]}Z ${dict("for")} ${validTimestamp}`, true);
            })
            .catch(err => {
                this.validIndicator.update(dict(err.message), false);
            })
            .finally(() => {
                this.loadingMeta = false;
                this._hideLoadingAnimationMaybe();
            });
        this.loadingMeta = true;
        this._armLoadingAnimation();
    },
    _updateBlipmap: function(geotiffUrls, parameter, renderOptions) {
        Promise.all(geotiffUrls.map(url => fetch(url + "?timestamp=" + this.parameters.timestamp)))
            .then(responses => {
                if (responses.every(response => response.ok)) {
                    return Promise.all(responses.map(response => response.arrayBuffer()));
                } else {
                    throw Error("dataMissing");
                }
            })
            .then(buffers => {
                return Promise.all(buffers.map(GeoTIFF.fromArrayBuffer));
            })
            .then(geotiffs => {
                return Promise.all(geotiffs.map(geotiff => geotiff.getImage()));
            })
            .then(images => {
                this.raspLayer.update(images, parameter, renderOptions);
            })
            .catch(err => {
                this.raspLayer.invalidate();
            })
            .finally(() => {
                this.loadingBlipmap = false;
                this._hideLoadingAnimationMaybe();
            });
        this.loadingBlipmap = true;
        this._armLoadingAnimation();
    },
    toggleMeteograms: function() {
        if (cDefaults.alwaysShowMeteograms || (this.meteogramCheckbox && this.meteogramCheckbox.checked)) {
            if (this.meteogramOverlay) {
                this.meteogramOverlay.addTo(this._map);
            }
        } else {
            if (this.currentPlot && this.currentPlot.type == "meteogram") {
                this.closePlot();
            }
            if (this.meteogramOverlay) {
                this.meteogramOverlay.remove();
            }
        }
    },
    disableAllMapSelectors: function() {
        if (this.crosssectionControl) {
            this.crosssectionControl.disable();
        }
        if (this.soundingControl) {
            this.soundingControl.disable();
        }
    },
    closePlot: function() {
        this.plot.style.display = "none";
        this._map.invalidateSize();
        this.currentPlot = null;
        if (this.crosssectionControl) {
            this.crosssectionControl.clear();
        }
        if (this.soundingControl) {
            this.soundingControl.clear();
        }
        this.togglePanelOrOffcanvas();
    },
    updatePlot: function() {
        this.plot.style.display = "";
        this.plotContent.innerHTML = "";
        if (this.currentPlot.type == "meteogram") {
            // 1. Create tab navigation using Bootstrap styles
            var tabList = L.DomUtil.create('ul', 'nav nav-tabs mb-2', this.plotContent);
            
            var itemMeteogram = L.DomUtil.create('li', 'nav-item', tabList);
            var btnMeteogram = L.DomUtil.create('button', 'nav-link' + (this.currentPlot.viewMode === "meteogram" ? " active" : ""), itemMeteogram);
            btnMeteogram.innerText = dict("Meteograms").split(" & ")[0];
            btnMeteogram.onclick = () => {
                this.currentPlot.viewMode = "meteogram";
                this.updatePlot();
            };
            
            var itemSounding = L.DomUtil.create('li', 'nav-item', tabList);
            var btnSounding = L.DomUtil.create('button', 'nav-link' + (this.currentPlot.viewMode === "sounding" ? " active" : ""), itemSounding);
            btnSounding.innerText = dict("sounding");
            if (!this.currentPlot.soundingData) {
                btnSounding.disabled = true;
                btnSounding.classList.add("disabled");
            }
            btnSounding.onclick = () => {
                this.currentPlot.viewMode = "sounding";
                this.updatePlot();
            };

            // 2. Display the selected mode content
            var displayContainer = L.DomUtil.create('div', 'plot-display', this.plotContent);
            if (this.currentPlot.viewMode === "meteogram") {
                if (this.currentPlot.image) {
                    this.currentPlot.image.style.objectFit = "contain";
                    displayContainer.appendChild(this.currentPlot.image);
                } else {
                    displayContainer.innerHTML = `<div class="p-3 text-center text-muted">${dict("dataMissing")}</div>`;
                }
            } else if (this.currentPlot.viewMode === "sounding") {
                var datetimeUTC = this.datetimeSelector.get().datetimeUTC;
                var soundingJson = this.currentPlot.soundingData[datetimeUTC] || this.currentPlot.soundingData[datetimeUTC.substring(0, 19) + "Z"];
                if (soundingJson) {
                    var skewtContainer = L.DomUtil.create('div', 'skewt-plot-container', displayContainer);
                    skewtContainer.id = "skewtPlotArea";
                    skewtContainer.style.width = "100%";
                    skewtContainer.style.height = "100%";
                    var skewt = new SkewT('#skewtPlotArea');
                    skewt.plot(soundingJson);
                } else {
                    displayContainer.innerHTML = `<div class="p-3 text-center text-muted">${dict("dataMissing")}</div>`;
                }
            }
        }
        this.loadingPlot = false;
        this._hideLoadingAnimationMaybe();
        this.togglePanelOrOffcanvas();
    },
    getMeteogramMarkers: function(modelKey) {
        var markers = [];
        var meteograms = cMeteograms[modelKey];
        for (const meteogramKey of Object.keys(meteograms)) {
            var meteogram = meteograms[meteogramKey];
            markers.push(
                L.marker(meteogram.location, {icon: this.meteogramIcon})
                    .bindTooltip(meteogram.name)
                    .on('click', e => {
                        var dir = this.datetimeSelector.get().dir;
                        this.currentPlot = {
                            type: "meteogram",
                            key: meteogramKey,
                            soundingUrl: cDefaults.forecastServerResults + "/OUT/" + dir + "/sounding_" + meteogramKey + ".json",
                            imageUrl: cDefaults.forecastServerResults + "/OUT/" + dir + "/meteogram_" + meteogramKey + ".png",
                            viewMode: (this.currentPlot && this.currentPlot.key === meteogramKey && this.currentPlot.viewMode) ? this.currentPlot.viewMode : "meteogram"
                        };
                        this.loadingPlot = true;
                        this._armLoadingAnimation();
                        
                        Promise.all([
                            fetch(this.currentPlot.soundingUrl)
                                .then(res => {
                                    if (res.ok) return res.json();
                                    throw new Error("soundings not available");
                                })
                                .catch(() => null),
                            new Promise((resolve) => {
                                let img = new Image();
                                img.onload = () => resolve(img);
                                img.onerror = () => resolve(null);
                                img.src = this.currentPlot.imageUrl;
                            })
                        ]).then(([soundingData, imageElement]) => {
                            this.currentPlot.soundingData = soundingData;
                            this.currentPlot.image = imageElement;
                            this.updatePlot();
                        }).catch(() => {
                            this.loadingPlot = false;
                            this._hideLoadingAnimationMaybe();
                        });
                    })
            );
        }
        return L.layerGroup(markers);
    }
});

export default function(options) {
    return new L.Control.RASPControl(options);
};
